import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface Season {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  rewards: string[];
  active: boolean;
  archived: boolean;
  createdAt: Date;
}

const Seasons: React.FC = () => {
  const { t } = useTranslation();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Season | null>(null);
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    rewards: '',
    active: false,
    archived: false
  });

  useEffect(() => {
    loadSeasons();
  }, []);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'seasons'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate(),
        endDate: doc.data().endDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      })) as Season[];
      setSeasons(data);
    } catch (error) {
      console.error('Error loading seasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        name: form.name,
        startDate: Timestamp.fromDate(new Date(form.startDate)),
        endDate: Timestamp.fromDate(new Date(form.endDate)),
        rewards: form.rewards.split('\n').filter(r => r.trim()),
        active: form.active,
        archived: form.archived,
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'seasons', editing.id), data);
      } else {
        await addDoc(collection(db, 'seasons'), {
          ...data,
          createdAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadSeasons();
    } catch (error) {
      console.error('Error saving season:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('leaderboard.confirmDeleteSeason'))) {
      try {
        await deleteDoc(doc(db, 'seasons', id));
        loadSeasons();
      } catch (error) {
        console.error('Error deleting season:', error);
      }
    }
  };

  const handleEdit = (season: Season) => {
    setEditing(season);
    setForm({
      name: season.name,
      startDate: season.startDate.toISOString().split('T')[0],
      endDate: season.endDate.toISOString().split('T')[0],
      rewards: season.rewards.join('\n'),
      active: season.active,
      archived: season.archived
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      startDate: '',
      endDate: '',
      rewards: '',
      active: false,
      archived: false
    });
  };

  const getSeasonStatus = (season: Season) => {
    const now = new Date();
    if (season.archived) return { label: t('leaderboard.archived'), color: 'bg-gray-500/20 text-gray-400' };
    if (season.active && now >= season.startDate && now <= season.endDate) {
      return { label: t('leaderboard.active'), color: 'bg-green-500/20 text-green-400' };
    }
    if (now < season.startDate) return { label: 'Upcoming', color: 'bg-blue-500/20 text-blue-400' };
    if (now > season.endDate) return { label: 'Ended', color: 'bg-yellow-500/20 text-yellow-400' };
    return { label: 'Inactive', color: 'bg-gray-500/20 text-gray-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('leaderboard.seasonManagement')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('leaderboard.newSeason')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {seasons.map(season => {
          const status = getSeasonStatus(season);
          return (
            <div key={season.id} className="card-dark p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">{season.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                    {status.label}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-[#B8B8C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[#B8B8C3]">{season.startDate.toLocaleDateString()}</span>
                  <span className="text-[#B8B8C3]">→</span>
                  <span className="text-[#B8B8C3]">{season.endDate.toLocaleDateString()}</span>
                </div>

                {season.rewards.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-[#B8B8C3] mb-2">
                      🏆 {season.rewards.length} {t('leaderboard.rewards')}
                    </p>
                    <div className="space-y-1">
                      {season.rewards.slice(0, 3).map((reward, idx) => (
                        <div key={idx} className="text-xs text-white bg-[#15191E] px-2 py-1 rounded">
                          {reward}
                        </div>
                      ))}
                      {season.rewards.length > 3 && (
                        <div className="text-xs text-[#B8B8C3]">
                          +{season.rewards.length - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(season)}
                  className="flex-1 btn-secondary text-sm"
                >
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDelete(season.id)}
                  className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {seasons.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🗓️</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('leaderboard.noSeasons')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('leaderboard.noSeasonsDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('leaderboard.createFirstSeason')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('leaderboard.editSeason') : t('leaderboard.newSeason')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.seasonName')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('leaderboard.seasonNamePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.startDate')}
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    max="9999-12-31"
                    className="input-dark w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.endDate')}
                  </label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    max="9999-12-31"
                    className="input-dark w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.rewards')}
                </label>
                <textarea
                  value={form.rewards}
                  onChange={(e) => setForm({ ...form, rewards: e.target.value })}
                  className="input-dark w-full"
                  rows={6}
                  placeholder={t('leaderboard.rewardsPlaceholder')}
                />
                <p className="text-xs text-[#B8B8C3] mt-1">{t('leaderboard.rewardsHint')}</p>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="seasonActive"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="seasonActive" className="text-sm text-[#B8B8C3]">
                    {t('leaderboard.activateSeason')}
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="seasonArchived"
                    checked={form.archived}
                    onChange={(e) => setForm({ ...form, archived: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="seasonArchived" className="text-sm text-[#B8B8C3]">
                    {t('leaderboard.archived')}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Seasons;
